import React, { useEffect } from 'react';
import PageLayout from '../../components/PageLayout';
import _t from 'counterpart';
import {
  CCard,
  CCardBody,
  CCol,
  CForm,
  CFormGroup,
  CFormText,
  CInputGroup,
  CLabel,
  CRow,
  CSelect,
  CSwitch,
  CTextarea,
} from '@coreui/react';
import { useLegacyState } from '../../helpers/customHooks';
import ButtonWithLoader from '../../components/ButtonWithLoader';
import { useMutation, useQuery } from 'react-query';
import {
  getMt5Settings,
  sendMessageToAllCustomers,
  sendMessageToCustomers,
  sendMt5PushToAllCustomers,
  sendMt5PushToCustomers,
  IMt5PushSendResult,
} from '../../services/BackendService';
import { findErrorFromValidation, handleFormError } from '../../helpers';
import toast from 'react-hot-toast';
import { useLocation } from 'react-router';

/** MT5 refuses push notifications above this many characters. */
const MT5_PUSH_MAX_LENGTH = 1024;
/** The in-app DTO limits (AdminCustomerMessageDTO); capped here too so a giant paste cannot
 *  blow past the server's body-size limit and come back as a bare "request entity too large". */
const INAPP_MESSAGE_MAX_LENGTH = 10000;
const SUBJECT_MAX_LENGTH = 160;

type Channel = 'inapp' | 'mt5push';

interface IState {
  message: string;
  accounts: string;
  sendToAll: boolean;
  subject: string;
  channel: Channel;
}

const MessagingPage = () => {
  const initialState: IState = {
    message: '',
    accounts: '',
    sendToAll: false,
    subject: '',
    channel: 'inapp',
  };

  const { state: queryState } = useLocation<{ customersIds: string }>();

  const [state, setState] = useLegacyState<IState>(initialState);

  const { accounts, message, sendToAll, subject, channel } = state;

  // Whether the MT5 push channel is offered at all; the deployment may not have MT5.
  const mt5SettingsQuery = useQuery(['mt5-settings'], () => getMt5Settings());

  const resetState = () => {
    setState(initialState);
  };

  const onMessagesSent = ({ messageRecipients }: { messageRecipients: number }) => {
    resetState();
    toast.success(
      _t('messaging.message-sent-to-customers', {
        sent: messageRecipients,
      })
    );
  };

  // The MT5 server only delivers to accounts with a registered MetaQuotes ID, so the
  // backend reports who was reachable — the admin sees the skips, not just a success.
  const onPushSent = (result: IMt5PushSendResult) => {
    resetState();
    toast.success(_t('messaging.push-sent-to-customers', { ...result }));
  };

  const sendMessageMutation = useMutation(
    ({ accounts, message, subject }: { accounts: string; message: string; subject: string }) =>
      sendMessageToCustomers(accounts, message, subject),
    {
      onSuccess: onMessagesSent,
      onError: (e: any) => {
        handleFormError(e);
      },
    }
  );

  const sendMessageAllMutation = useMutation(
    ({ message, subject }: { message: string; subject: string }) => sendMessageToAllCustomers(message, subject),
    {
      onSuccess: onMessagesSent,
      onError: (e: any) => {
        handleFormError(e);
      },
    }
  );

  const sendMt5PushMutation = useMutation(
    ({ accounts, message }: { accounts: string; message: string }) => sendMt5PushToCustomers(accounts, message),
    {
      onSuccess: onPushSent,
      onError: (e: any) => {
        handleFormError(e);
      },
    }
  );

  const sendMt5PushAllMutation = useMutation(
    ({ message }: { message: string }) => sendMt5PushToAllCustomers(message),
    {
      onSuccess: onPushSent,
      onError: (e: any) => {
        handleFormError(e);
      },
    }
  );

  const allMutations = [sendMessageMutation, sendMessageAllMutation, sendMt5PushMutation, sendMt5PushAllMutation];
  const isSending = allMutations.some((mutation) => mutation.isLoading);

  // A draft can exceed the push cap only by switching channel with a longer in-app text already
  // typed (maxLength blocks typing, not pre-existing state). Refuse to send it instead of letting
  // the server bounce it after a round trip.
  const overPushLimit = channel === 'mt5push' && message.length > MT5_PUSH_MAX_LENGTH;

  // Without the resets, findError keeps showing a previous attempt's validation error — the first
  // mutation in the list that ever failed would shadow the current channel's.
  const resetMutationErrors = () => {
    allMutations.forEach((mutation) => mutation.reset());
  };

  const handleChannelChanged = (event: React.ChangeEvent<HTMLSelectElement>) => {
    resetMutationErrors();
    setState({
      channel: event.currentTarget.value as Channel,
    });
  };

  const handleMessageChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    setState({
      message: event.currentTarget.value,
    });
  };

  const handleSubjectChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    setState({
      subject: event.currentTarget.value,
    });
  };

  const handleAccountsChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    setState({
      accounts: event.currentTarget.value,
    });
  };

  const toggleSendToAll = () => {
    setState({
      sendToAll: !sendToAll,
    });
  };

  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    resetMutationErrors();
    if (channel === 'mt5push') {
      if (sendToAll) {
        sendMt5PushAllMutation.mutate({ message });
      } else {
        sendMt5PushMutation.mutate({ accounts, message });
      }
    } else if (sendToAll) {
      sendMessageAllMutation.mutate({ message, subject });
    } else {
      sendMessageMutation.mutate({ accounts, message, subject });
    }
  };

  const findError = (paramName: string) => {
    for (const mutation of allMutations) {
      if (mutation.error) {
        return findErrorFromValidation(mutation.error, paramName);
      }
    }
    return undefined;
  };

  useEffect(() => {
    if (!queryState?.customersIds || queryState?.customersIds.length === 0) return;

    setState({
      ...state,
      accounts: queryState?.customersIds,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryState]);

  return (
    <PageLayout title={_t.translate('sidebar.messaging')}>
      <CRow>
        <CCol>
          <CCard>
            <CCardBody>
              <CForm onSubmit={sendMessage}>
                {mt5SettingsQuery.data?.mt5Enabled && (
                  <CFormGroup>
                    <CLabel htmlFor="nf-channel">{_t('messaging.channel')}</CLabel>
                    <CSelect id="nf-channel" name="nf-channel" value={channel} onChange={handleChannelChanged}>
                      <option value="inapp">{_t('messaging.channel-inapp')}</option>
                      <option value="mt5push">{_t('messaging.channel-mt5push')}</option>
                    </CSelect>
                  </CFormGroup>
                )}
                <div className="d-flex align-items-center mb-3">
                  <CSwitch id="send-all" color="primary" variant="3d" onChange={toggleSendToAll} checked={sendToAll} />
                  <span className="ml-2">{_t('messaging.send-to-all')}</span>
                </div>
                <CFormGroup>
                  <CLabel htmlFor="nf-accounts">{_t('messaging.user-ids')}</CLabel>
                  <CInputGroup>
                    <CTextarea
                      style={{ height: 120 }}
                      id="nf-accounts"
                      name="nf-accounts"
                      value={accounts || ''}
                      onChange={handleAccountsChanged}
                      disabled={sendToAll}
                    />
                  </CInputGroup>
                  <CFormText className="help-block">{_t('messaging.user-ids-comment')}</CFormText>
                  {findError('customers') && <CLabel className="text-danger">{findError('customers')}</CLabel>}
                </CFormGroup>
                {channel === 'inapp' && (
                  <CFormGroup>
                    <CLabel htmlFor="nf-subject">{_t('emails.subject')}</CLabel>
                    <CInputGroup>
                      <CTextarea
                        style={{ height: 40 }}
                        id="nf-subject"
                        name="nf-subject"
                        value={subject || ''}
                        onChange={handleSubjectChanged}
                        maxLength={SUBJECT_MAX_LENGTH}
                      />
                    </CInputGroup>
                    {findError('subject') && <CLabel className="text-danger">{findError('subject')}</CLabel>}
                  </CFormGroup>
                )}
                <CFormGroup>
                  <CLabel htmlFor="nf-message">{_t('messaging.message')}</CLabel>
                  <CInputGroup>
                    <CTextarea
                      style={{ height: 120 }}
                      id="nf-message"
                      name="nf-message"
                      value={message || ''}
                      onChange={handleMessageChanged}
                      maxLength={channel === 'mt5push' ? MT5_PUSH_MAX_LENGTH : INAPP_MESSAGE_MAX_LENGTH}
                    />
                  </CInputGroup>
                  {channel === 'mt5push' && (
                    <CFormText className={`help-block text-right${overPushLimit ? ' text-danger' : ''}`}>
                      {message.length}/{MT5_PUSH_MAX_LENGTH}
                    </CFormText>
                  )}
                  {findError('message') && <CLabel className="text-danger">{findError('message')}</CLabel>}
                </CFormGroup>

                <ButtonWithLoader
                  isLoading={isSending}
                  disabled={overPushLimit}
                  type="submit"
                  buttonColor="primary"
                  spinnerColor="secondary"
                  title={_t('action.send')}
                  className="mr-2"
                />
              </CForm>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
    </PageLayout>
  );
};

export default React.memo(MessagingPage);
